/**
 * `/share` — "Share to Viko" (PLAN G15). Two ways in, one compact screen:
 *  - the PWA Web Share Target (`POST /share/target` → service worker stash → `?stashed=1`), and
 *  - the URL handoff for iOS Shortcuts (`?project=&text=&url=&title=`; text only).
 * Pick a project (recent first), add an optional note or dictation, Send. The item is a sealed
 * bark (Quick) with attachments through the encrypted artifact path; offline it waits in the
 * bark outbox and goes out exactly once on reconnect.
 */
import { Link } from '@tanstack/react-router'
import { Button } from '@viko/transcript-ui'
import { Check, Mic, MicOff, Paperclip, Send } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { AttachmentDropZone } from '../components/AttachmentDropZone.js'
import { useBarkStore, useTriageMachine } from '../components/bark/useBarks.js'
import { storage } from '../components/Capture.js'
import { errorText, useToast } from '../components/ui.js'
import { LARGE_TEXT_BYTES, useAttachmentInput } from '../lib/attachment-input.js'
import { rememberProject } from '../lib/last-project.js'
import { useProjects } from '../lib/queries.js'
import { matchProject, noteRecentProject, orderByRecent } from '../lib/recent-projects.js'
import { parseShareParams, type SharedPayload, takeShared } from '../lib/share-target.js'
import { useDictation } from '../lib/speech.js'

export function SharePage() {
  const [shared, setShared] = useState<(SharedPayload & { project?: string }) | undefined>()
  useEffect(() => {
    let alive = true
    void (async () => {
      const q = parseShareParams(location.search)
      const stash = q.stashed ? await takeShared() : { text: '', files: [] as File[] }
      if (!alive) return
      const text = [stash.text, q.text].filter(Boolean).join('\n')
      setShared({ text, files: stash.files, ...(q.project ? { project: q.project } : {}) })
      // Keep the address bar clean (and a refresh from re-sending the handoff text).
      history.replaceState(null, '', '/share')
    })()
    return () => {
      alive = false
    }
  }, [])
  if (!shared)
    return (
      <div role="status" aria-busy="true" className="p-4">
        Opening…
      </div>
    )
  return <ShareToViko shared={shared} />
}

export function ShareToViko({ shared }: { shared: SharedPayload & { project?: string } }) {
  const store = useBarkStore()
  const toast = useToast()
  const projects = useProjects()
  const list = useMemo(() => orderByRecent(projects.data ?? []), [projects.data])
  const preset = matchProject(list, shared.project)
  const [picked, setPicked] = useState<string | undefined>()
  const projectId = picked ?? preset?.id ?? list[0]?.id
  const project = list.find((p) => p.id === projectId)
  const machineId = useTriageMachine(projectId ?? '')
  const [note, setNote] = useState('')
  const [dictated, setDictated] = useState(false)
  const [sending, setSending] = useState(false)
  const [queued, setQueued] = useState(false)
  const [done, setDone] = useState<string | undefined>()
  const filePicker = useRef<HTMLInputElement>(null)
  const input = useAttachmentInput({
    storageOff: storage.off,
    largeTextBytes: LARGE_TEXT_BYTES,
    insertText: (t) => setNote((cur) => cur + t),
  })
  const dictation = useDictation((t) => {
    setDictated(true)
    setNote((cur) => (cur.trim() ? `${cur.replace(/\s+$/, '')} ${t}` : t))
  })

  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current || !shared.files.length) return
    seeded.current = true
    input.add(shared.files)
  }, [shared.files, input.add])

  const empty = !shared.text && !shared.files.length
  const body = [shared.text, note.trim()].filter(Boolean).join('\n\n')
  const canSend =
    !!projectId && !!machineId && !sending && !!store && (!!body || !!input.files.length)

  const send = async () => {
    if (!canSend || !projectId || !machineId || !store) return
    if (!input.canSend)
      return toast('Remove or fix the attachments marked with an error first.', 'error')
    dictation.stop()
    setSending(true)
    input.markUploading(true)
    try {
      await store.submit({
        projectId,
        machineId,
        text: body,
        mode: 'quick',
        dictated,
        files: input.files,
        source: 'capture',
      })
      rememberProject(projectId)
      noteRecentProject(projectId)
      setQueued(typeof navigator !== 'undefined' && navigator.onLine === false)
      setDone(projectId)
      input.clear()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      input.retryable()
      input.markUploading(false)
      setSending(false)
    }
  }

  if (done)
    return (
      <section className="mx-auto grid w-full max-w-lg gap-4 p-4" aria-label="Share to Viko">
        <p role="status" className="m-0 flex items-center gap-2 text-[16px] font-medium">
          <Check size={18} aria-hidden="true" />
          {queued ? 'Queued offline — sends when you’re back' : 'Sent — Viko is sorting it'}
        </p>
        <div className="flex gap-2">
          <Link to="/p/$projectId" params={{ projectId: done }} className="underline">
            Open {list.find((p) => p.id === done)?.name ?? 'project'}
          </Link>
          <Link to="/home" className="underline">
            Back to Home
          </Link>
        </div>
      </section>
    )

  return (
    <section
      className="mx-auto grid w-full max-w-lg gap-4 p-4"
      aria-label="Share to Viko"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault()
          void send()
        }
      }}
    >
      <h1 className="m-0 text-[20px] font-semibold">Share to Viko</h1>

      {empty ? (
        <p className="m-0 rounded-lg bg-surface-2 px-3 py-2 text-[14px]">
          Nothing arrived with this share. Write a note or attach a file to send it anyway.
        </p>
      ) : shared.text ? (
        <blockquote
          aria-label="Shared text"
          className="m-0 whitespace-pre-wrap break-words rounded-lg bg-surface-2 px-3 py-2 text-[14px]"
        >
          {shared.text}
        </blockquote>
      ) : null}

      <section aria-label="Project" className="grid gap-1">
        <h2 className="m-0 text-[14px] font-medium">Project</h2>
        {projects.isPending ? (
          <p className="m-0 text-[14px]">Loading projects…</p>
        ) : !list.length ? (
          <p className="m-0 text-[14px]">No projects yet. Create one in Viko first.</p>
        ) : (
          <div role="radiogroup" aria-label="Project" className="grid gap-1">
            {list.map((p, i) => (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={p.id === projectId}
                onClick={() => setPicked(p.id)}
                className={`flex min-h-11 items-center justify-between rounded-lg border px-3 text-left text-[15px] ${
                  p.id === projectId ? 'border-accent bg-accent-soft' : 'border-line'
                }`}
              >
                <span>{p.name}</span>
                {i === 0 ? <span className="text-[14px] text-muted">last used</span> : null}
              </button>
            ))}
          </div>
        )}
      </section>

      <AttachmentDropZone input={input} className="grid gap-2">
        <label className="grid gap-1 text-[14px] font-medium">
          Note (optional)
          <textarea
            className="min-h-24 rounded-lg border border-line bg-surface px-3 py-2 text-[15px] font-normal"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onPaste={input.onPaste}
            placeholder="What should Viko do with this?"
          />
        </label>
        <div className="flex gap-2">
          {dictation.supported ? (
            <Button size="sm" variant="ghost" onClick={dictation.toggle}>
              {dictation.listening ? (
                <MicOff size={14} aria-hidden="true" />
              ) : (
                <Mic size={14} aria-hidden="true" />
              )}
              {dictation.listening ? 'Stop dictation' : 'Dictate'}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => filePicker.current?.click()}>
            <Paperclip size={14} aria-hidden="true" /> Attach
          </Button>
        </div>
      </AttachmentDropZone>
      <input
        ref={filePicker}
        type="file"
        multiple
        hidden
        aria-label="Attach files"
        onChange={(e) => {
          input.add(e.target.files)
          e.target.value = ''
        }}
      />

      {!machineId && projectId ? (
        <p role="alert" className="m-0 text-[14px]">
          No machine can sort this yet. Add a machine to {project?.name ?? 'this project'}.
        </p>
      ) : null}

      <Button variant="primary" disabled={!canSend} onClick={() => void send()}>
        <Send size={14} aria-hidden="true" />{' '}
        {sending ? 'Sending…' : `Send to ${project?.name ?? 'project'}`}
      </Button>
    </section>
  )
}
